'use client';

import { HTMLAttributes, useMemo, useState } from 'react';
import Ping from '@/components/common/Ping';
import { cn } from '@/lib/utils';
import { ChevronDown, Triangle } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useRouter } from 'next/navigation';
import { uniqBy } from 'lodash';
import useTimeWindowList, {
  formatEndTime,
  type TimeWindowItem,
} from '../hooks/use-time-window-list';
import { formatDateTime } from '@/lib/utils/date';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ResolvedButtonsProps {
  window: TimeWindowItem;
  onChange: (timestamp: number) => void;
}

function groupTimestamps(
  items: TimeWindowItem[],
  selected: TimeWindowItem,
): {
  before: TimeWindowItem[];
  middle: TimeWindowItem[];
  after: TimeWindowItem[];
} {
  if (items.length === 0) {
    return { before: [], middle: [], after: [] };
  }

  const selectedItem = items.find((b) => b.slug === selected.slug) || selected;
  const activeItem = items.find((b) => b.active);
  const activeIndex = items.findIndex((b) => b.slug === activeItem?.slug);
  if (activeIndex === -1) {
    if (!selectedItem) return { before: items, middle: [], after: [] };

    return {
      before: items.filter((item) => item.slug !== selectedItem.slug),
      middle: [selected],
      after: [],
    };
  }
  let middle = [items[activeIndex - 1], items[activeIndex], items[activeIndex + 1]];
  if (selectedItem && activeItem) {
    const isSelectedPast = selectedItem.startTs < activeItem.startTs;
    if (isSelectedPast) {
      middle.unshift(selectedItem);
    } else {
      middle.push(selectedItem);
    }
  }
  return {
    before: items.slice(0, activeIndex - 1).reverse(),
    middle: uniqBy(middle.filter(isTimeWindowItem), 'slug'),
    after: items.slice(activeIndex + 2),
  };
}

function isTimeWindowItem(item: TimeWindowItem | undefined): item is TimeWindowItem {
  return Boolean(item);
}

export default function ResolvedButtons(props: ResolvedButtonsProps) {
  const { window } = props;
  const router = useRouter();
  const { currentLanguage } = useTranslation();
  const [beforeOpen, setBeforeOpen] = useState(false);
  const [afterOpen, setAfterOpen] = useState(false);
  const [selected, setSelected] = useState<TimeWindowItem>(window);
  const { windows, nowWindowStartTs, outcomesBySlug } = useTimeWindowList(window.slug);
  console.log(windows);
  const { before, middle, after } = useMemo(
    () => groupTimestamps(windows, window),
    [windows, selected, nowWindowStartTs],
  );
  const { t } = useTranslation();

  const formatStr =
    currentLanguage === 'en'
      ? 'h:mm A [ET]'
      : ['zh-CN', 'zh-TW'].includes(currentLanguage)
        ? 'A h:mm [ET]'
        : 'hh:mm';

  const handleSelected = (item: TimeWindowItem) => {
    setSelected(item);
    router.push(`/predic/market?id=${item.slug}`);
  };

  return (
    <div className="py-3 w-full">
      <div className="flex gap-3 items-center">
        <div
          className="flex gap-3 items-center relative"
          onMouseEnter={() => setBeforeOpen(true)}
          onMouseLeave={() => setBeforeOpen(false)}
        >
          <StyledButton>
            {t('Past')} <ChevronDown strokeWidth={3} size={16} />
          </StyledButton>
          <div
            className={cn(
              'absolute left-0 bottom-8 flex-col gap-1 bg-[#1E2124] rounded-lg max-h-50 overflow-scroll hidden p-2',
              beforeOpen ? 'flex' : '',
            )}
          >
            {before.map((b) => (
              <StyledButton key={b.slug} onClick={() => handleSelected(b)}>
                <Outcome outcome={outcomesBySlug[b.slug]} />
                {formatLabel(b, formatStr)}
              </StyledButton>
            ))}
          </div>
        </div>
        {middle.map((b) => (
          <StyledButton
            key={b.slug}
            onClick={() => handleSelected(b)}
            className={
              b.slug === selected.slug ? 'bg-white text-black hover:bg-white hover:text-black' : ''
            }
          >
            <Outcome outcome={outcomesBySlug[b.slug]} />
            {b.startTs === nowWindowStartTs && <Ping />}
            {formatVisibleLabel(b, formatStr.replace(/\s+\[ET\]$/, ''))}
          </StyledButton>
        ))}
        {after.length > 3 ? (
          <div
            className="flex gap-3 items-center relative"
            onMouseEnter={() => setAfterOpen(true)}
            onMouseLeave={() => setAfterOpen(false)}
          >
            <StyledButton>
              {t('More')} <ChevronDown strokeWidth={3} size={16} />
            </StyledButton>
            <div
              className={cn(
                'absolute left-0 bottom-8 flex-col gap-1 bg-[#1E2124] rounded-lg max-h-50 overflow-scroll hidden p-2',
                afterOpen ? 'flex' : '',
              )}
            >
              {after.map((b) => (
                <StyledButton key={b.slug} onClick={() => handleSelected(b)}>
                  <Outcome outcome={outcomesBySlug[b.slug]} />
                  {formatLabel(b, formatStr)}
                </StyledButton>
              ))}
            </div>
          </div>
        ) : (
          <>
            {after.map((b) => (
              <StyledButton key={b.slug} onClick={() => handleSelected(b)}>
                {formatLabel(b, formatStr)}
              </StyledButton>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function StyledButton(props: HTMLAttributes<HTMLButtonElement>) {
  const { children, className, ...rest } = props;
  return (
    <button
      className={cn(
        'rounded-lg px-2 py-1.5 bg-[#1E2124] hover:bg-[#131517] hover:text-white text-sm flex items-center gap-2 whitespace-nowrap cursor-pointer pointer-events-auto',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

function Outcome({ outcome }: { outcome?: string }) {
  if (!outcome) return null;
  return (
    <div
      className={cn('inline-flex items-center justify-center rounded-full text-white size-4.5', {
        'bg-red-600': outcome === 'down',
        'bg-green-600': outcome === 'up',
      })}
    >
      <Triangle size={8} fill="white" className={cn(outcome === 'down' ? 'rotate-180' : '')} />
    </div>
  );
}

function formatLabel(window: TimeWindowItem, formatStr: string) {
  if (window.endTs && (/-\d{10}$/.test(window.slug) || /-et$/.test(window.slug))) {
    const et = dayjs.unix(window.endTs).tz('America/New_York');
    const todayEt = dayjs().tz('America/New_York');

    return `${formatDateTime(et, formatStr)} · ${getEtDiffLabel(et, todayEt)}`;
  }
  return window.endTs ? formatDateTime(window.endTs * 1000, 'MMM D, YYYY') : window.label;
}

function getEtDiffLabel(et: dayjs.Dayjs, todayEt: dayjs.Dayjs) {
  const diffDays = et.startOf('day').diff(todayEt, 'day');
  if (diffDays === 0) {
    return 'Today';
  }

  if (diffDays === 1) {
    return 'Tomorrow';
  }

  if (!et.isSame(todayEt, 'year')) {
    return et.format('MMM D, YYYY');
  }

  return et.format('MMM D');
}

function formatVisibleLabel(window: TimeWindowItem, formatStr: string) {
  if (window.endTs && (/-\d{10}$/.test(window.slug) || /-et$/.test(window.slug))) {
    const et = dayjs.unix(window.endTs).tz('America/New_York');

    return `${formatDateTime(et, formatStr)}`;
  }
  return window.endTs ? formatEndTime(window.endTs * 1000) : window.label;
}
